import { useState, useRef, useEffect } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { MessageCircle, X, Send, Loader2, Minimize2, Maximize2, Sparkles } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function AiAssistant() {
  const [open, setOpen] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content: "Hi! I'm your IPVALUE Assistant. I can help you with:\n\n- **Using any feature** in the system\n- **Facebook Marketplace** tips (photos, descriptions, ads)\n- **Live data** — ask me about stock levels, sales, sellers\n- **Business advice** for your store\n\nHow can I help you today?"
    }
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [conversationId] = useState(() => `conv_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    if (open && !minimized) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open, minimized]);

  const sendMessage = async (e) => {
    e?.preventDefault();
    const msg = input.trim();
    if (!msg || loading) return;

    setInput("");
    setMessages(prev => [...prev, { role: "user", content: msg }]);
    setLoading(true);

    try {
      const res = await fetch(`${API}/api/ai/assistant`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ message: msg, conversation_id: conversationId }),
      });

      if (!res.ok) throw new Error("Failed");
      const data = await res.json();
      setMessages(prev => [...prev, { role: "assistant", content: data.response }]);
    } catch {
      setMessages(prev => [...prev, {
        role: "assistant",
        content: "Sorry, I couldn't process that. Please try again or contact support@ipvaluemanagements.com"
      }]);
    } finally {
      setLoading(false);
    }
  };

  // Render markdown-like formatting
  const formatMessage = (text) => {
    if (!text) return null;
    return text.split("\n").map((line, i) => {
      // Bold
      let formatted = line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
      // Bullet points
      if (formatted.startsWith("- ")) {
        return <div key={i} className="flex gap-2 ml-2" dangerouslySetInnerHTML={{ __html: `<span class="text-primary">•</span><span>${formatted.slice(2)}</span>` }} />;
      }
      if (formatted.trim() === "") return <br key={i} />;
      return <p key={i} dangerouslySetInnerHTML={{ __html: formatted }} />;
    });
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-6 right-6 z-50 h-14 w-14 rounded-full bg-primary text-primary-foreground shadow-lg hover:shadow-xl transition-all hover:scale-105 flex items-center justify-center"
        data-testid="ai-assistant-trigger"
      >
        <MessageCircle className="h-6 w-6" />
      </button>
    );
  }

  if (minimized) {
    return (
      <div
        className="fixed bottom-6 right-6 z-50 bg-card border rounded-lg shadow-lg cursor-pointer hover:shadow-xl transition-shadow flex items-center gap-3 px-4 py-3"
        onClick={() => setMinimized(false)}
        data-testid="ai-assistant-minimized"
      >
        <Sparkles className="h-4 w-4 text-primary" />
        <span className="text-sm font-medium">Chat with Charles</span>
        <Maximize2 className="h-4 w-4 text-muted-foreground" />
      </div>
    );
  }

  return (
    <div
      className="fixed bottom-6 right-6 z-50 w-[400px] max-w-[calc(100vw-2rem)] bg-card border rounded-lg shadow-2xl flex flex-col"
      style={{ height: "min(600px, calc(100vh - 6rem))" }}
      data-testid="ai-assistant-panel"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b bg-primary/5 rounded-t-lg">
        <div className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-primary" />
          <div>
            <p className="text-sm font-semibold">Chat with Charles</p>
            <p className="text-xs text-muted-foreground">My team can also help</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" onClick={() => setMinimized(true)} className="h-8 w-8 p-0">
            <Minimize2 className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setOpen(false)} className="h-8 w-8 p-0">
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4">
        {messages.map((msg, i) => (
          <div
            key={i}
            className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
            data-testid={`chat-message-${i}`}
          >
            <div
              className={`max-w-[85%] rounded-lg px-3 py-2 text-sm leading-relaxed ${
                msg.role === "user"
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted"
              }`}
            >
              {msg.role === "user" ? (
                <p>{msg.content}</p>
              ) : (
                <div className="space-y-1 assistant-msg">{formatMessage(msg.content)}</div>
              )}
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex justify-start">
            <div className="bg-muted rounded-lg px-4 py-3">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Thinking...
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <form onSubmit={sendMessage} className="px-3 py-3 border-t flex gap-2">
        <Input
          ref={inputRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Ask me anything..."
          className="rounded-full text-sm"
          disabled={loading}
          data-testid="ai-assistant-input"
        />
        <Button
          type="submit"
          size="sm"
          disabled={loading || !input.trim()}
          className="rounded-full h-9 w-9 p-0 shrink-0"
          data-testid="ai-assistant-send"
        >
          <Send className="h-4 w-4" />
        </Button>
      </form>
    </div>
  );
}
